import React from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography as MuiTypography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import moment from "moment";

interface Video {
  id: string;
  title?: string;
  description?: string;
  youtubeUrl?: string;
  youtubeVideoId?: string;
  fileUrl?: string;
  thumbnail?: string;
  duration?: string;
  uploadedAt: string;
  uploadedBy?: string;
}

interface VideoPlayerProps {
  video: Video | null;
  open: boolean;
  onClose: () => void;
}

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }: IStyledArguments) => ({
  "& .MuiDialog-paper": {
    maxWidth: "1200px",
    width: "90%",
    background: theme.palette.extra.default,
    borderRadius: "16px",
    overflow: "hidden",
  },
}));

const DialogHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const HeaderActions = styled(Box)({
  display: "flex",
  gap: "8px",
});

const StyledDialogContent = styled(DialogContent)(() => ({
  padding: 0,
  overflow: "hidden",
}));

const VideoContainer = styled(Box)({
  position: "relative",
  width: "100%",
  paddingTop: "56.25%", // 16:9 aspect ratio
  background: "#000",
});

const VideoElement = styled("video")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  border: "none",
  background: "#000",
});

const VideoIframe = styled("iframe")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  border: "none",
});

const VideoDetails = styled(Box)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(3),
}));

const VideoTitle = styled(MuiTypography)({
  fontWeight: 700,
  fontSize: "1.25rem",
  marginBottom: "12px",
});

const VideoMeta = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: "16px",
  marginBottom: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#666",
}));

const MetaItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const VideoDescription = styled(MuiTypography)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  whiteSpace: "pre-wrap",
  lineHeight: 1.6,
}));

/**
 * Extracts YouTube video ID from URL
 */
const getYouTubeEmbedUrl = (videoId: string): string => {
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
};

/**
 * Checks if video is a YouTube video
 */
const isYouTubeVideo = (video: Video): boolean => {
  return !!(video.youtubeVideoId || video.youtubeUrl);
};

/**
 * Formats uploaded date
 */
const formatDate = (dateString: string): string => {
  return moment(dateString).format("MMM D, YYYY");
};

/**
 * VideoPlayer Component
 * Modal component to play YouTube videos with details
 */
const VideoPlayer: React.FC<VideoPlayerProps> = ({ video, open, onClose }) => {
  if (!video) return null;

  const handleOpenExternal = () => {
    const url = video.youtubeUrl || video.fileUrl;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="video-player-dialog"
    >
      <DialogHeader>
        <Box flex={1}>
          <MuiTypography variant="h6" style={{ fontWeight: 600 }}>
            Match Video
          </MuiTypography>
        </Box>
        
        <HeaderActions>
          {(video.youtubeUrl || video.fileUrl) && (
            <IconButton
              onClick={handleOpenExternal}
              size="small"
              title={isYouTubeVideo(video) ? "Open in YouTube" : "Open in new tab"}
              style={{ color: "#666" }}
            >
              <OpenInNewIcon />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            size="small"
            title="Close"
            style={{ color: "#666" }}
          >
            <CloseIcon />
          </IconButton>
        </HeaderActions>
      </DialogHeader>

      <StyledDialogContent>
        <VideoContainer>
          {isYouTubeVideo(video) && video.youtubeVideoId ? (
            <VideoIframe
              src={getYouTubeEmbedUrl(video.youtubeVideoId)}
              title={video.title || "Match Video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : video.fileUrl ? (
            <VideoElement
              src={video.fileUrl}
              title={video.title || "Match Video"}
              controls
              preload="metadata"
            />
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              color="#666"
            >
              <MuiTypography>Video not available</MuiTypography>
            </Box>
          )}
        </VideoContainer>

        <VideoDetails>
          <VideoTitle>
            {video.title || "Match Video"}
          </VideoTitle>

          <VideoMeta>
            <MetaItem>
              <span>Uploaded {formatDate(video.uploadedAt)}</span>
            </MetaItem>
            {video.uploadedBy && (
              <MetaItem>
                <span>•</span>
                <span>By {video.uploadedBy}</span>
              </MetaItem>
            )}
          </VideoMeta>

          {video.description && (
            <VideoDescription variant="body2">
              {video.description}
            </VideoDescription>
          )}
        </VideoDetails>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default VideoPlayer;
